export interface HostLocation {
  readonly origin: string;
  readonly hostname: string;
}

const SECURE_FRAME_PROTOCOLS: readonly string[] = ['https:'];
const INSECURE_FRAME_PROTOCOLS: readonly string[] = ['https:', 'http:'];

function frameProtocols(allowInsecure: boolean): readonly string[] {
  return allowInsecure ? INSECURE_FRAME_PROTOCOLS : SECURE_FRAME_PROTOCOLS;
}

/** Lowercase, without the root-label dot, so `www.cars.com.` is `www.cars.com`. */
export function canonicalHostname(hostname: string): string {
  return hostname.toLowerCase().replace(/\.$/, '');
}

/**
 * The origin of a `src` template, or undefined when the template is not an
 * absolute https URL (http only when `allowInsecure`) with a literal authority,
 * no credentials and no backslash, which URL parsing would read as a slash.
 */
export function templateOrigin(
  template: string,
  allowInsecure = false,
): string | undefined {
  const scheme = template.indexOf('://');
  if (scheme === -1 || template.includes('\\')) {
    return undefined;
  }
  const authority = template.slice(scheme + 3).split(/[/?#]/, 1)[0] ?? '';
  if (/[{}@]/.test(authority)) {
    return undefined;
  }
  let url: URL;
  try {
    url = new URL(template);
  } catch {
    return undefined;
  }
  if (
    !frameProtocols(allowInsecure).includes(url.protocol) ||
    url.username ||
    url.password
  ) {
    return undefined;
  }
  return url.origin;
}

/**
 * The canonical origin of a declared origin string, or undefined when it is
 * not a bare origin of an allowed protocol (path, query, fragment or
 * credentials).
 */
export function declaredOrigin(
  raw: string,
  allowInsecure: boolean,
): string | undefined {
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return undefined;
  }
  if (
    !frameProtocols(allowInsecure).includes(url.protocol) ||
    url.username ||
    url.password ||
    url.search ||
    url.hash ||
    url.pathname !== '/'
  ) {
    return undefined;
  }
  return url.origin;
}

export interface OriginRefusalOptions {
  readonly allowInsecure?: boolean;
  /** The entry's `hostServed` opt-in: the origin may be the host page's own. */
  readonly hostServed?: boolean;
}

/**
 * Why an iframe or script `src` origin is refused, or undefined when it is
 * allowed. The host page's own origin or hostname is refused unless the entry
 * opts in with `hostServed`; every origin must be declared either way.
 */
export function originRefusal(
  origin: string,
  origins: readonly string[],
  host: HostLocation,
  options: OriginRefusalOptions = {},
): string | undefined {
  const allowInsecure = options.allowInsecure === true;
  if (
    options.hostServed !== true &&
    (origin === host.origin ||
      canonicalHostname(new URL(origin).hostname) ===
        canonicalHostname(host.hostname))
  ) {
    return 'is the host page itself, and the entry is not hostServed';
  }
  if (
    !origins.some(
      (declared) => declaredOrigin(declared, allowInsecure) === origin,
    )
  ) {
    return "is not one of the entry's origins";
  }
  return undefined;
}

/**
 * Final labels that are known public top-level domains, where the label
 * before them is a registrable name, so `a.cars.com` and `b.partner.com` are
 * not siblings. Every two-letter label is a country code and counts as well.
 * Any other final label may be an internal or local suffix, which no list
 * describes, so two hosts that share it are treated as siblings.
 */
const PUBLIC_TLDS: ReadonlySet<string> = new Set([
  'com',
  'net',
  'org',
  'edu',
  'gov',
  'mil',
  'int',
  'info',
  'biz',
  'name',
  'pro',
  'mobi',
  'aero',
  'asia',
  'coop',
  'museum',
  'travel',
  'jobs',
  'cat',
  'tel',
  'xxx',
  'app',
  'dev',
  'page',
  'online',
  'site',
  'store',
  'shop',
  'tech',
  'cloud',
  'xyz',
  'blog',
  'news',
  'agency',
  'digital',
  'media',
  'education',
  'school',
  'university',
  'college',
  'academy',
  'example',
]);

const COUNTRY_CODE = /^[a-z]{2}$/;

function isPublicTld(label: string): boolean {
  return COUNTRY_CODE.test(label) || PUBLIC_TLDS.has(label);
}

function labels(hostname: string): string[] {
  return canonicalHostname(hostname).split('.');
}

/** An IPv4 or IPv6 literal, whose labels say nothing about cookie scope. */
function isIpLiteral(hostname: string): boolean {
  const host = canonicalHostname(hostname);
  return host.includes(':') || /^\d{1,3}(\.\d{1,3}){3}$/.test(host);
}

function lastTwoLabels(parts: readonly string[]): string {
  return parts.slice(-2).join('.');
}

/**
 * True when the two hostnames may share cookies by domain. It errs toward
 * true, which only asks for confirmation more often:
 * - Under a suffix like `co.uk` it matches unrelated sites, because it keeps
 *   no public suffix list.
 * - Single-label names, and hosts whose final label is not a known public
 *   top-level domain (`localhost`, `corp`, `localdomain`, `test`, ...), are
 *   siblings when they share that final label, because their parent is a
 *   single label that no two-label comparison can tell apart.
 * - An IP literal is a sibling only of the same address.
 */
export function isSiblingSubdomain(
  hostname: string,
  hostHostname: string,
): boolean {
  if (isIpLiteral(hostname) || isIpLiteral(hostHostname)) {
    return canonicalHostname(hostname) === canonicalHostname(hostHostname);
  }
  const a = labels(hostname);
  const b = labels(hostHostname);
  const lastA = a[a.length - 1] ?? '';
  const lastB = b[b.length - 1] ?? '';
  if (
    lastA === lastB &&
    (a.length === 1 || b.length === 1 || !isPublicTld(lastA))
  ) {
    return true;
  }
  return lastTwoLabels(a) === lastTwoLabels(b);
}
